import { useMemo, useState } from "react";
import { FileText, Download, Calendar, Filter, Sparkles } from "lucide-react";
import { formatIDR } from "@/lib/constants";
import { computeExpectedNet, computeTotalReceived } from "@/lib/paymentReceipts";
import { useAuth } from "@/lib/auth";
import IncomeStatementReport from "./IncomeStatementReport";
import BalanceSheetReport from "./BalanceSheetReport";

/**
 * Laporan Tab — 149-cw-r119 (2026-08-29, updated r121 2026-08-29).
 * 2026-10-18 (user request P2): Income Statement & Neraca otomatis aktif.
 * Filter periode bulan + preview ringkasan bulanan on-screen.
 * Pembukuan bertahap menggantikan software akuntansi eksternal.
 */
export default function LaporanTab({ canExport, projects, loading }) {
  const today = new Date();
  const currentMonth = today.toISOString().slice(0, 7);
  const [monthFrom, setMonthFrom] = useState(currentMonth);
  const [monthTo, setMonthTo] = useState(currentMonth);
  // 2026-10-18 (user request): sub-tab Laba Rugi & Neraca aktif untuk semua
  // pemegang perm `finance_report.view` — bukan hanya BOD Lv1.
  const { user } = useAuth();
  const [sub, setSub] = useState("ringkasan");
  const [asOf, setAsOf] = useState(() => {
    const now = new Date();
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return last.toISOString().slice(0, 10);
  });

  const monthlyBreakdown = useMemo(() => {
    // Group income & receipts & expenses by month for range.
    const map = new Map();
    const inRange = (dstr) => {
      if (!dstr) return false;
      const ym = dstr.slice(0, 7);
      return ym >= monthFrom && ym <= monthTo;
    };
    for (const p of projects) {
      for (const e of (p.income_entries || [])) {
        const ym = (e.invoice_date || "").slice(0, 7);
        if (!inRange(e.invoice_date)) continue;
        const cur = map.get(ym) || { month: ym, invoiced: 0, received: 0, expense: 0 };
        cur.invoiced += Number(e.amount) || 0;
        cur.received += computeTotalReceived(e);
        map.set(ym, cur);
      }
      for (const x of (p.expense_entries || [])) {
        if (x.excluded_from_totals === true) continue;
        const ym = (x.date || "").slice(0, 7);
        if (!inRange(x.date)) continue;
        const cur = map.get(ym) || { month: ym, invoiced: 0, received: 0, expense: 0 };
        cur.expense += Number(x.amount) || 0;
        map.set(ym, cur);
      }
    }
    return Array.from(map.values()).sort((a, b) => a.month.localeCompare(b.month));
  }, [projects, monthFrom, monthTo]);

  const grand = monthlyBreakdown.reduce(
    (s, m) => ({ invoiced: s.invoiced + m.invoiced, received: s.received + m.received, expense: s.expense + m.expense }),
    { invoiced: 0, received: 0, expense: 0 }
  );
  const grandNet = grand.received - grand.expense;

  return (
    <div className="space-y-4" data-testid="fin-laporan-tab">
      <div className="border-b border-slate-200 flex gap-1" role="tablist" data-testid="fin-laporan-subtabs">
        <SubTabBtn active={sub === "ringkasan"} onClick={() => setSub("ringkasan")} testid="fin-laporan-sub-ringkasan">Ringkasan Bulanan</SubTabBtn>
        <SubTabBtn active={sub === "income"} onClick={() => setSub("income")} testid="fin-laporan-sub-income-statement">
          Laba Rugi
        </SubTabBtn>
        <SubTabBtn active={sub === "neraca"} onClick={() => setSub("neraca")} testid="fin-laporan-sub-neraca">
          Neraca
        </SubTabBtn>
      </div>

      {sub === "ringkasan" && (<>
      {/* Filter bar */}
      <div className="border border-slate-200 bg-white p-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="overline text-slate-600 block mb-1">Dari Bulan</label>
          <input
            type="month"
            value={monthFrom}
            onChange={(e) => setMonthFrom(e.target.value)}
            data-testid="fin-laporan-from"
            className="border border-slate-300 text-xs px-2 py-1.5 focus:outline-none focus:border-[#FF5A1F]"
          />
        </div>
        <div>
          <label className="overline text-slate-600 block mb-1">Sampai Bulan</label>
          <input
            type="month"
            value={monthTo}
            onChange={(e) => setMonthTo(e.target.value)}
            data-testid="fin-laporan-to"
            className="border border-slate-300 text-xs px-2 py-1.5 focus:outline-none focus:border-[#FF5A1F]"
          />
        </div>
        <button
          type="button"
          disabled={!canExport}
          data-testid="fin-laporan-export"
          onClick={() => alert("Ekspor ke PDF/Excel sedang disiapkan.")}
          className="h-9 px-4 border border-[#0B1D3A] bg-[#0B1D3A] text-white hover:bg-[#122a4d] text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Download size={13} /> Ekspor Laporan
        </button>
      </div>

      {/* Preview table */}
      <div className="border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b-2 border-slate-300">
            <tr className="text-left">
              <th className="px-3 py-2 overline text-slate-700">Periode</th>
              <th className="px-3 py-2 overline text-slate-700 text-right">Total Ditagihkan</th>
              <th className="px-3 py-2 overline text-slate-700 text-right">Diterima</th>
              <th className="px-3 py-2 overline text-slate-700 text-right">Pengeluaran</th>
              <th className="px-3 py-2 overline text-slate-700 text-right">Net</th>
            </tr>
          </thead>
          <tbody>
            {monthlyBreakdown.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-slate-400 italic">Tidak ada data di rentang bulan yang dipilih.</td></tr>
            )}
            {monthlyBreakdown.map((m) => {
              const net = m.received - m.expense;
              return (
                <tr key={m.month} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`fin-laporan-row-${m.month}`}>
                  <td className="px-3 py-2 tabular-nums font-semibold text-[#0B1D3A]">{formatMonthLabel(m.month)}</td>
                  <td className="px-3 py-2 tabular-nums text-right text-slate-800 font-semibold">{formatIDR(m.invoiced)}</td>
                  <td className="px-3 py-2 tabular-nums text-right text-emerald-700 font-semibold">{formatIDR(m.received)}</td>
                  <td className="px-3 py-2 tabular-nums text-right text-rose-700 font-semibold">{formatIDR(m.expense)}</td>
                  <td className={`px-3 py-2 tabular-nums text-right font-bold ${net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatIDR(net)}</td>
                </tr>
              );
            })}
          </tbody>
          {monthlyBreakdown.length > 0 && (
            <tfoot className="bg-slate-50 border-t-2 border-slate-300">
              <tr>
                <td className="px-3 py-2 overline text-[#0B1D3A] font-bold">Grand Total</td>
                <td className="px-3 py-2 tabular-nums text-right font-bold text-slate-900">{formatIDR(grand.invoiced)}</td>
                <td className="px-3 py-2 tabular-nums text-right font-bold text-emerald-700">{formatIDR(grand.received)}</td>
                <td className="px-3 py-2 tabular-nums text-right font-bold text-rose-700">{formatIDR(grand.expense)}</td>
                <td className={`px-3 py-2 tabular-nums text-right font-bold ${grandNet >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{formatIDR(grandNet)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="bg-slate-50 border border-slate-200 p-3 text-[11px] text-slate-600 leading-relaxed" data-testid="fin-laporan-note">
        Halaman ini bertahap menjadi sumber laporan keuangan resmi — Laba Rugi &amp; Neraca otomatis dari transaksi engine A.R., invoice terverifikasi, dan petty cash approved. Gunakan sub-tab untuk melihat laporan standar.
      </div>
      </>)}

      {sub === "income" && (
        <IncomeStatementSection monthFrom={monthFrom} setMonthFrom={setMonthFrom}
          monthTo={monthTo} setMonthTo={setMonthTo} canExport={canExport} />
      )}
      {sub === "neraca" && (
        <BalanceSheetSection asOf={asOf} setAsOf={setAsOf} canExport={canExport} />
      )}
    </div>
  );
}

function IncomeStatementSection({ monthFrom, setMonthFrom, monthTo, setMonthTo, canExport }) {
  return (
    <div className="space-y-3" data-testid="fin-laporan-income-section">
      <div className="border border-slate-200 bg-white p-3 flex flex-wrap items-end gap-3">
        <div><label className="overline text-slate-600 block mb-1">Dari Bulan</label>
          <input type="month" value={monthFrom} onChange={(e) => setMonthFrom(e.target.value)}
            data-testid="is-month-from" className="border border-slate-300 text-xs px-2 py-1.5" /></div>
        <div><label className="overline text-slate-600 block mb-1">Sampai Bulan</label>
          <input type="month" value={monthTo} onChange={(e) => setMonthTo(e.target.value)}
            data-testid="is-month-to" className="border border-slate-300 text-xs px-2 py-1.5" /></div>
      </div>
      <IncomeStatementReport monthFrom={monthFrom} monthTo={monthTo} canExport={canExport} />
    </div>
  );
}

function BalanceSheetSection({ asOf, setAsOf, canExport }) {
  return (
    <div className="space-y-3" data-testid="fin-laporan-neraca-section">
      <div className="border border-slate-200 bg-white p-3 flex flex-wrap items-end gap-3">
        <div><label className="overline text-slate-600 block mb-1">Per Tanggal</label>
          <input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)}
            data-testid="bs-as-of" className="border border-slate-300 text-xs px-2 py-1.5" /></div>
      </div>
      <BalanceSheetReport asOf={asOf} canExport={canExport} />
    </div>
  );
}

function SubTabBtn({ active, onClick, testid, children }) {
  return (
    <button type="button" onClick={onClick} role="tab" aria-selected={active} data-testid={testid}
      className={`px-4 py-2 text-xs font-semibold border transition-colors ${active ? "bg-[#0B1D3A] text-white border-[#0B1D3A]" : "bg-white text-slate-600 border-transparent hover:bg-slate-100 hover:text-[#0B1D3A]"}`}>
      {children}
    </button>
  );
}

function EmptySubReport({ title, testid }) {
  return (
    <div className="border border-dashed border-slate-300 bg-white p-10 text-center" data-testid={testid}>
      <FileText size={28} className="mx-auto text-slate-300 mb-2" />
      <div className="font-display text-sm font-semibold text-[#0B1D3A]">{title}</div>
      <p className="text-[11px] text-slate-500 mt-1">Fitur sedang disiapkan — konten laporan akan tampil di sini. Placeholder ini hanya terlihat oleh admin (BOD Lv1).</p>
    </div>
  );
}

function formatMonthLabel(ym) {
  if (!ym) return "-";
  const [y, m] = ym.split("-");
  const MONTHS = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
  return `${MONTHS[Number(m) - 1] || m} ${y}`;
}
